/**
 * Модель карты данных: тождество узла, его место, фильтры, раскладка по видам группировки и
 * связи. Тождество узла — тип и id (mapNodeKey): сервис 7 и база 7 — разные узлы в выборе,
 * связях и кеше. Место узла — регион, кластер и признак внешнего контура (макет
 * map-topology.js:36-53): регион, кластер и namespace — независимые атрибуты; «Регион не
 * указан», «Внешний контур» и «Без кластера» — служебные группы, а не настоящие id. Кластер —
 * id токена DIM, имя — из словаря сервера. Связь хранит обе стороны потока и инициатора соединения:
 * обратный поток не теряется, направление связи — от инициатора, а не от типов данных; шифрование null —
 * неизвестно, а не «нет TLS». Вид группировки меняет только раскладку, состав узлов и связей
 * тот же.
 */
import type { DataMapElement, MapLink, MapNodeRef } from '@/api/dataMap';
import type { ResourceRef } from '@/api/workspaceTypes';
import { RISK_HIGH_ABOVE, RISK_MEDIUM_ABOVE } from '@/components/ResourceRisk/riskLevels';
import type { GroupingMode, MapFilters, NodeKind } from './types';
import { EXTERNAL_REGION, NO_CLUSTER, UNKNOWN_REGION } from './types';

export function mapNodeKey(type: string, id: string): string {
	return `${type}/${id}`;
}

export function elementKey(element: Pick<DataMapElement, 'type' | 'id'>): string {
	return mapNodeKey(element.type, String(element.id));
}

export function linkEndKeys(link: MapLink): [string, string] {
	return [mapNodeKey(link.left.type, link.left.id), mapNodeKey(link.right.type, link.right.id)];
}

export function linksOfNode(key: string, links: MapLink[]): MapLink[] {
	return links.filter((link) => linkEndKeys(link).includes(key));
}

/** Тип узла сервиса; остальные типы узлов — хранилища */
const SERVICE_NODE_TYPE = 'resource';

/**
 * Тип узла прежней карты -> ресурс каталога. Объектное хранилище прежней карты — бакет
 * каталога (обе — строки qhub_s3_buckets); внешний сервис — карточка сервиса.
 */
const RESOURCE_KIND_OF_NODE: Record<string, ResourceRef['kind']> = {
	[SERVICE_NODE_TYPE]: 'service',
	sql_db_database: 'sql_database',
	s3_bucket: 's3_bucket',
	s3_object_store: 's3_bucket',
	kafka_instance: 'kafka_instance',
	nosql_db_database: 'nosql_database',
};

export function mapNodeResource(node: MapNodeRef): ResourceRef | null {
	const kind = RESOURCE_KIND_OF_NODE[node.type];
	return kind ? { kind, id: node.id } : null;
}

/* ────────────────────────────────────────────
 * Узел и его место
 * ──────────────────────────────────────────── */

export interface MapNode {
	key: string;
	element: DataMapElement;
	kind: NodeKind;
	/** Внешний сервис: стоит во внешнем контуре, кластер к нему не применяется */
	external: boolean;
	/** Регион, «Регион не указан» (UNKNOWN_REGION) или «Внешний контур» (EXTERNAL_REGION) */
	regionId: string;
	/** Кластер или «Без кластера» (NO_CLUSTER) */
	clusterId: string;
	/** Оценка риска доступа по модели вида ресурса; null — оценки нет */
	score: number | null;
}

/** Ключ кластера элемента: числовой рабочий или строковый учебный ID; null — без кластера */
export function clusterKey(element: Pick<DataMapElement, 'cluster_id'>): string | null {
	const id = element.cluster_id;
	return id === null || id === undefined || id === 0 || id === '' || Number(id) < 0
		? null
		: String(id);
}

export function toMapNode(element: DataMapElement): MapNode {
	const external = element.data.is_external;
	return {
		key: elementKey(element),
		element,
		kind: element.type === SERVICE_NODE_TYPE ? 'service' : 'storage',
		external,
		regionId: external ? EXTERNAL_REGION : element.region || UNKNOWN_REGION,
		clusterId: clusterKey(element) ?? NO_CLUSTER,
		score: element.risk?.score ?? null,
	};
}

/** Высокий риск доступа — выше 60 из 100, как счётчик каталога и метка макета (n.risk > 60) */
export function isHighRisk(node: Pick<MapNode, 'score'>): boolean {
	return node.score !== null && node.score > RISK_HIGH_ABOVE;
}

/** Уровень риска каталога: 3 — высокий, 2 — средний, 1 — низкий, 0 — оценки нет */
function riskBand(score: number | null): number {
	if (score === null) return 0;
	if (score > RISK_HIGH_ABOVE) return 3;
	return score > RISK_MEDIUM_ABOVE ? 2 : 1;
}

/** Шкала оценки, как risk_scale каталога: у SQL-базы — своя (sql), у остальных хранилищ — storage */
function riskScale(node: MapNode): string {
	return mapNodeResource({ type: node.element.type, id: '' })?.kind === 'sql_database'
		? 'sql'
		: 'storage';
}

/**
 * Порядок риска каталога (HeterogeneousResourceQuery.orderBy): уровень по убыванию, затем шкала, и
 * лишь внутри шкалы — оценка по убыванию; равные — по виду и id каталога. Оценки разных моделей
 * между собой не сравниваются: 81 по модели хранилищ не «больше» 73 по модели SQL
 */
export function byCatalogRisk(a: MapNode, b: MapNode): number {
	const kindOf = (node: MapNode) =>
		mapNodeResource({ type: node.element.type, id: '' })?.kind ?? node.element.type;
	return (
		riskBand(b.score) - riskBand(a.score) ||
		riskScale(a).localeCompare(riskScale(b)) ||
		(b.score ?? 0) - (a.score ?? 0) ||
		kindOf(a).localeCompare(kindOf(b)) ||
		Number(a.element.id) - Number(b.element.id) ||
		String(a.element.id).localeCompare(String(b.element.id))
	);
}

/* ────────────────────────────────────────────
 * Фильтры
 * ──────────────────────────────────────────── */

/** Нормализация строки поиска макета (toLocaleLowerCase('ru')) */
export function searchKey(text: string): string {
	return text.toLocaleLowerCase('ru').trim();
}

/**
 * Узел виден при фильтрах (макет visibleMapNodes): поиск по тексту узла (имя, теги данных,
 * регион, кластер, namespace — его собирает место показа), тип узла, высокий риск, регион и
 * кластер; у внешнего узла кластера нет, фильтр кластера его скрывает
 */
export function nodeMatches(node: MapNode, filters: MapFilters, searchText: string): boolean {
	const query = searchKey(filters.q);
	if (query && !searchKey(searchText).includes(query)) return false;
	if (filters.kind && node.kind !== filters.kind) return false;
	if (filters.highRisk && !isHighRisk(node)) return false;
	if (filters.region && node.regionId !== filters.region) return false;
	if (filters.cluster && (node.external || node.clusterId !== filters.cluster)) return false;
	return true;
}

/**
 * Переход к связанному узлу снимает только те фильтры, которые его скрывают (спецификация :148):
 * поиск, тип узла, высокий риск, регион и кластер — по отдельности; вид группировки остаётся
 */
export function revealFilters(filters: MapFilters, node: MapNode, searchText: string): MapFilters {
	const query = searchKey(filters.q);
	return {
		q: query && !searchKey(searchText).includes(query) ? '' : filters.q,
		kind: filters.kind && node.kind !== filters.kind ? '' : filters.kind,
		highRisk: filters.highRisk && !isHighRisk(node) ? false : filters.highRisk,
		region: filters.region && node.regionId !== filters.region ? '' : filters.region,
		cluster:
			filters.cluster && (node.external || node.clusterId !== filters.cluster)
				? ''
				: filters.cluster,
	};
}

/* ────────────────────────────────────────────
 * Раскладка
 * ──────────────────────────────────────────── */

export interface ClusterBlock {
	id: string;
	regionId: string;
	services: MapNode[];
	storages: MapNode[];
	/** Две колонки — сервисы слева, хранилища справа; одна — общий столбец (макет mapScene) */
	columns: 1 | 2;
}

export interface RegionBlock {
	id: string;
	clusters: ClusterBlock[];
	/** Ресурсов в регионе */
	count: number;
}

/** Регионы с вложенными кластерами и внешний контур — для видов регионов и кластеров */
export interface LocationScene {
	layout: 'location';
	external: MapNode[];
	regions: RegionBlock[];
}

/** Три зоны вида «По типам ресурсов»: внешний контур, приложения, хранилища */
export interface TypeScene {
	layout: 'types';
	external: MapNode[];
	services: MapNode[];
	storages: MapNode[];
}

export type MapScene = LocationScene | TypeScene;

const byName = (a: MapNode, b: MapNode) =>
	a.element.name.localeCompare(b.element.name, 'ru', { numeric: true }) ||
	a.key.localeCompare(b.key);

function groupBy(nodes: MapNode[], key: (node: MapNode) => string): Map<string, MapNode[]> {
	const groups = new Map<string, MapNode[]>();
	for (const node of nodes) {
		const list = groups.get(key(node)) ?? [];
		list.push(node);
		groups.set(key(node), list);
	}
	return groups;
}

/**
 * Служебная группа — в конце. При переданном словаре — по имени и ключу, без зависимости
 * от счётчиков. Прежние виды сохраняют порядок по размеру из макета.
 */
function groupOrder(
	groups: Map<string, MapNode[]>,
	sentinel: string,
	names?: ReadonlyMap<string, string>,
): string[] {
	const size = (key: string) => groups.get(key)?.length ?? 0;
	return [...groups.keys()].sort((a, b) => {
		if ((a === sentinel) !== (b === sentinel)) return a === sentinel ? 1 : -1;
		if (names)
			return (
				(names.get(a) ?? a).localeCompare(names.get(b) ?? b, 'ru', { numeric: true }) ||
				a.localeCompare(b)
			);
		return size(b) - size(a) || a.localeCompare(b, undefined, { numeric: true });
	});
}

function locationScene(
	nodes: MapNode[],
	clusterNames?: ReadonlyMap<string, string>,
): LocationScene {
	const inside = nodes.filter((node) => !node.external);
	const byRegion = groupBy(inside, (node) => node.regionId);
	const regions = groupOrder(byRegion, UNKNOWN_REGION, clusterNames ? new Map() : undefined).map(
		(regionId) => {
			const members = byRegion.get(regionId) ?? [];
			const byCluster = groupBy(members, (node) => node.clusterId);
			const clusterIds = groupOrder(byCluster, NO_CLUSTER, clusterNames);
			const clusters = clusterIds.map((clusterId): ClusterBlock => {
				const inCluster = (byCluster.get(clusterId) ?? []).sort(byName);
				return {
					id: clusterId,
					regionId,
					services: inCluster.filter((node) => node.kind === 'service'),
					storages: inCluster.filter((node) => node.kind === 'storage'),
					columns: clusterIds.length === 1 || inCluster.length > 2 ? 2 : 1,
				};
			});
			return { id: regionId, clusters, count: members.length };
		},
	);
	return {
		layout: 'location',
		external: nodes.filter((node) => node.external).sort(byName),
		regions,
	};
}

/** Раскладка видимых узлов по виду группировки; состав узлов один при любом виде */
export function mapScene(
	nodes: MapNode[],
	grouping: GroupingMode,
	clusterNames: ReadonlyMap<string, string> = new Map(),
): MapScene {
	if (grouping !== 'resource-type')
		return locationScene(nodes, grouping === 'namespace' ? clusterNames : undefined);
	return {
		layout: 'types',
		external: nodes.filter((node) => node.external).sort(byName),
		services: nodes.filter((node) => !node.external && node.kind === 'service').sort(byName),
		storages: nodes.filter((node) => node.kind === 'storage').sort(byName),
	};
}

/** Узлы сцены в порядке раскладки */
export function sceneNodes(scene: MapScene): MapNode[] {
	if (scene.layout === 'types') return [...scene.external, ...scene.services, ...scene.storages];
	return [
		...scene.external,
		...scene.regions.flatMap((region) =>
			region.clusters.flatMap((cluster) => [...cluster.services, ...cluster.storages]),
		),
	];
}

/* ────────────────────────────────────────────
 * Связи
 * ──────────────────────────────────────────── */

/** Контекст связи макета (mapConnectionContext): внешний узел важнее различия регионов */
export type EdgeContext = 'external' | 'crossRegion' | 'crossCluster' | 'cluster';

export function edgeContext(a: MapNode, b: MapNode): EdgeContext {
	if (a.external || b.external) return 'external';
	if (a.regionId !== b.regionId) return 'crossRegion';
	if (a.clusterId !== b.clusterId) return 'crossCluster';
	return 'cluster';
}

/**
 * Рискованная связь — чувствительные данные идут без TLS: шифрование известно и отсутствует,
 * а типы данных по связи наблюдались (в макете — HTTP от внешнего входа, map.js:16-17)
 */
export function isRiskyLink(link: MapLink): boolean {
	return (
		link.isEncrypted === false &&
		link.leftToRightDataTypes.length + link.rightToLeftDataTypes.length > 0
	);
}

export interface MapEdge {
	key: string;
	link: MapLink;
	left: MapNode;
	right: MapNode;
	context: EdgeContext;
	risky: boolean;
	/** Связь выбранного узла */
	highlighted: boolean;
	/** Выбран другой узел: связь приглушена */
	dimmed: boolean;
}

/** Связи между видимыми узлами: связь рисуется, когда на карте оба её конца */
export function mapEdges(
	links: MapLink[],
	visible: ReadonlyMap<string, MapNode>,
	selectedKey: string | null,
): MapEdge[] {
	const hasSelection = selectedKey !== null && visible.has(selectedKey);
	const edges: MapEdge[] = [];
	for (const link of links) {
		const [leftKey, rightKey] = linkEndKeys(link);
		const left = visible.get(leftKey);
		const right = visible.get(rightKey);
		if (!left || !right) continue;
		const highlighted = hasSelection && (leftKey === selectedKey || rightKey === selectedKey);
		edges.push({
			key: `${leftKey}|${rightKey}`,
			link,
			left,
			right,
			context: edgeContext(left, right),
			risky: isRiskyLink(link),
			highlighted,
			dimmed: hasSelection && !highlighted,
		});
	}
	return edges;
}

/** Узлы, связанные с выбранным, вместе с ним самим: остальные приглушаются (макет map.js:146) */
export function relatedKeys(selectedKey: string, edges: MapEdge[]): Set<string> {
	const related = new Set([selectedKey]);
	for (const edge of edges) {
		if (edge.left.key === selectedKey) related.add(edge.right.key);
		if (edge.right.key === selectedKey) related.add(edge.left.key);
	}
	return related;
}

/**
 * Направление связи со стороны узла, как в инспекторе макета (s === id — «Исходящая»): соединение
 * открывает узел, его открывают к узлу, обе стороны или инициатор неизвестен
 */
export type LinkDirection = 'outgoing' | 'incoming' | 'both' | 'unknown';

export function directionFrom(link: MapLink, ownKey: string): LinkDirection {
	if (link.connection === 'both') return 'both';
	if (link.connection === 'unknown') return 'unknown';
	const ownIsLeft = linkEndKeys(link)[0] === ownKey;
	return (link.connection === 'left_to_right') === ownIsLeft ? 'outgoing' : 'incoming';
}
